import { useSessionUser } from "hooks/useSession";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import { isAxiosError } from "axios";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { PermissionGuard } from "components/common";
import { useVersionSettings } from "hooks/useProjects/useVersions";
import { useScriptAiRuns } from "hooks/project/useScriptAiRuns";
import {
  effectiveHasCharacterBreakdownForRun,
  effectiveHasCoverageForRun,
  effectiveHasScriptBreakdownForRun,
} from "utils/effectiveScriptBreakdown";
import { resolveRunAiHubPipelineVisibility } from "utils/runAiHubPipelineVisibility";
import {
  areScheduleBudgetRunPrerequisitesMet,
  isCharacterBreakdownCompleteForScheduleOrBudget,
  isScriptBreakdownCompleteForScheduleOrBudget,
} from "utils/scheduleBudgetRunPrerequisites";
import { tagDefinitionsReportHasAppliedUsage } from "utils/tagDefinitionsReportApplied";
import { useTagDefinitionsReport } from "../hooks/use-tag-definitions-report";
import {
  getAiCreditsActionRerunAvailability,
  isRunAiCreditsUnlimited,
} from "utils/aiCreditsAvailability";
import { getAiCreditsWalletBadge } from "components/common/AiCreditsIndicator";
import { ENVS } from "config";
import {
  getProjectFeatureStatus,
  handleToast,
  mapProjectFeatureStatusesToFeatureStatus,
} from "utils";
import { isAiFeatureStatusRunning } from "@/components/common/AiPipelineRunning";
import { ServerError } from "utils/genericUtilityFunctions";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS, type ScriptAiHubPipelineKey } from "analytics";
import { status as statusEnum } from "store/types/enum";
import type { ScriptAiHubDialogProps } from "./ScriptAiHubDialog.types";
import type { RunAiHubPipeline } from "./projectScriptAiHubContext";
import { ReRunDayBreaksModal } from "components/common/modals/stripboard/components/ReRunDayBreaksModal/ReRunDayBreaksModal";
import EditBudgetAssumptionsModal from "components/dashboard/body/projects/panels/budget/EditBudgetAssumptionsModal";
import { useStripboardApi } from "hooks/useStripboard";
import { useBudgetApi } from "hooks/useBudget";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { PurchaseProjectModal } from "components/common/modals/PurchaseProjectModal";
import { useCreateProjectPermissionGuard } from "components/common/CreateProjectPermissionGuard";
import { Permissions } from "utils/permissions/permissionConstants";
import { AiRunMergePolicy, isBreakdownAlreadyTaggedError } from "utils/aiRunMergePolicy";
import type {
  BudgetCategory,
  BudgetI,
} from "components/dashboard/body/projects/panels/budget/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import { Accordion, AccordionContent, AccordionItem } from "components/common/accordion/Accordion";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  Calendar,
  ChevronDown,
  DollarSign,
  FileText,
  Info,
  Layers,
  Loader2,
  Users,
  Zap,
} from "lucide-react";
import { cn } from "utils/cn";
import {
  getRunBatchTooltip,
  getScriptPipelineRowTooltip,
  SCRIPT_AI_HUB_CREDITS_EXHAUSTED_TOOLTIP,
  SCRIPT_AI_HUB_VERSION_CHARACTER_OFF_TOOLTIP,
  SCRIPT_AI_HUB_VERSION_COVERAGE_OFF_TOOLTIP,
  canAffordScriptPipelineBatch,
} from "./scriptAiHubRunTooltips";

const KEY_SCRIPT = "script_breakdown_rerun";
const KEY_CHARACTER = "character_breakdown_rerun";
const KEY_COVERAGE = "coverage_rerun";
const KEY_SCHEDULE = "scheduling_rerun";
const KEY_BUDGET = "budget_rerun";

function scriptAiHubMutationErrorCode(error: unknown): string {
  if (isAxiosError(error)) {
    return ServerError.fromAxiosError(error).code ?? "unknown";
  }
  return "unknown";
}

const SCHEDULE_BUDGET_PRIOR_SCRIPT_TOOLTIP =
  "Finish script breakdown before running schedule or budget AI.";

const SCHEDULE_BUDGET_PRIOR_CHARACTER_TOOLTIP =
  "Finish character breakdown before running schedule or budget AI.";

const SCHEDULE_BUDGET_TAGS_CHECKING_TOOLTIP = "Checking whether the script has tags…";

const SCHEDULE_BUDGET_REQUIRE_TAGS_TOOLTIP =
  "Add tags to the script first, or run Script breakdown above to tag it automatically.";

/** Shown on the same line as schedule/budget credit counts (not a separate status). */
const SCHEDULE_BUDGET_META_HELPER = "Needs tags · Script breakdown";

const FEATURE_INFO_SCRIPT_BREAKDOWN =
  "Adds production tags (cast, props, locations, and more). Schedule and budget AI use these tags.";

/** single_run: script breakdown alone was refused. batch: the batch skipped it and started the rest. */
type ScriptTaggedNotice = "single_run" | "batch";

type ScriptTaggedState = {
  /** `${projectId}:${documentId}` the refusal came from. */
  identity: string;
  knownTagged: boolean;
  notice: ScriptTaggedNotice | null;
};

const SCRIPT_ALREADY_TAGGED_MESSAGE: Record<ScriptTaggedNotice, string> = {
  single_run:
    "This version already has tags, so script breakdown didn't run and used no credits. Your tags were kept. Overwriting re-tags the whole script and replaces your manual tag edits.",
  batch:
    "This version already has tags, so script breakdown didn't run and used no credits. Your tags were kept. The other pipelines in the batch started and use their normal credits. Overwriting re-tags the whole script and replaces your manual tag edits.",
};

const FEATURE_INFO_CHARACTER_BREAKDOWN =
  "Builds the character list and dialogue context from your script.";

const FEATURE_INFO_COVERAGE = "Synopsis, notes, and analysis you can share with collaborators.";

const FEATURE_INFO_SCHEDULE =
  "Suggests shoot days and stripboard order. Needs script tags; Script breakdown applies them.";

const FEATURE_INFO_BUDGET =
  "Estimates line items from your assumptions and tags. Needs script tags; Script breakdown applies them.";

/** Outer wrapper for pipelines + schedule + budget (single flush stack). */
const SCRIPT_AI_HUB_CARD_SURFACE =
  "overflow-hidden rounded-lg border border-border bg-card shadow-none";

const SCRIPT_PIPELINES_ACCORDION_ITEM_CLASS =
  "border-0 bg-transparent shadow-none rounded-none overflow-visible";

/** Outline lightning (stroke only) for Run AI hub actions. */
const HUB_ZAP_CLASS = "fill-none text-primary";

type RowProps = {
  icon: ReactNode;
  label: string;
  meta: string;
  isRunning: boolean;
  disabled: boolean;
  pendingLabel?: string;
  onRun: () => void;
  tooltip?: string;
  /** Short explainer next to the title (info icon + tooltip). */
  featureInfo?: string;
  /** Extra classes for the row container (e.g. vertical padding). */
  rowClassName?: string;
  /**
   * `inline-with-title`: amber icon tile left of title+meta (schedule/budget; matches accordion title stack height).
   * `leading`: neutral icon tile left of title+meta (script pipeline rows).
   */
  iconPlacement?: "leading" | "inline-with-title";
  /** The row's Run button, for returning focus to it. */
  runButtonRef?: Ref<HTMLButtonElement>;
  /** The dialog was opened for this pipeline (a step's Generate button). */
  preselected?: boolean;
};

const FeatureRow = ({
  icon,
  label,
  meta,
  isRunning,
  disabled,
  pendingLabel,
  onRun,
  tooltip,
  featureInfo,
  rowClassName,
  iconPlacement = "leading",
  runButtonRef,
  preselected = false,
}: RowProps) => {
  const button = (
    <Button
      ref={runButtonRef}
      type="button"
      variant="badge"
      color="brand"
      size="sm"
      disabled={disabled}
      onClick={onRun}
      className="h-9 w-9 min-w-0 shrink-0 p-0 text-primary hover:text-primary"
      aria-label={pendingLabel ?? `Run ${label}`}
    >
      {isRunning ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Zap className={cn("h-4 w-4", HUB_ZAP_CLASS)} aria-hidden />
      )}
    </Button>
  );

  const titleInfo = featureInfo ? (
    <Tooltip placement="top">
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 rounded text-muted-foreground/70 outline-none transition-colors hover:text-foreground/80 focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label={`About ${label}`}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-left leading-snug">{featureInfo}</TooltipContent>
    </Tooltip>
  ) : null;

  return (
    <div
      data-pipeline-row={label}
      data-preselected={preselected || undefined}
      aria-current={preselected ? "true" : undefined}
      className={cn(
        "flex items-center justify-between gap-3 py-3",
        rowClassName,
        preselected && "-mx-2 rounded-md bg-selected/60 px-2 ring-1 ring-inset ring-border"
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {iconPlacement === "inline-with-title" ? (
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span
              className="relative z-0 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted/50 text-primary"
              aria-hidden
            >
              {icon}
            </span>
            <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 flex-1 items-center justify-start">
                <div className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                  <span className="min-w-0 truncate text-sm font-medium text-foreground">
                    {label}
                  </span>
                  {titleInfo}
                </div>
              </div>
              <span className="truncate text-xs text-muted-foreground">{meta}</span>
            </div>
          </div>
        ) : (
          <>
            <span
              className="relative z-0 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted/50 text-foreground/80"
              aria-hidden
            >
              {icon}
            </span>
            <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 flex-1 items-center justify-start">
                <div className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                  <span className="min-w-0 truncate text-sm font-medium text-foreground">
                    {label}
                  </span>
                  {titleInfo}
                </div>
              </div>
              <span className="truncate text-xs text-muted-foreground">{meta}</span>
            </div>
          </>
        )}
      </div>
      {tooltip ? (
        <Tooltip placement="top">
          <TooltipTrigger>
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center">
              {button}
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </div>
  );
};

export const ScriptAiHubDialog = ({
  open,
  onOpenChange,
  projectId,
  documentId,
  project,
  scriptHasMinimalRunContent,
  persistDraftBeforeRun,
  preselectedPipeline = null,
}: ScriptAiHubDialogProps) => {
  const { data: versionSettingsPayload } = useVersionSettings(projectId, documentId);
  const runs = useScriptAiRuns(projectId, documentId);
  const [runAllStep, setRunAllStep] = useState<string | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [licensePurchaseModalOpen, setLicensePurchaseModalOpen] = useState(false);
  const [forceOverwriteAdvanced, setForceOverwriteAdvanced] = useState(false);
  const [scriptPipelinesAccordion, setScriptPipelinesAccordion] =
    useState<string>("script-pipelines");

  const { session: profile, isOneTimePurchase } = useSessionUser();
  const isFreePlan = profile?.pricing_plan_id === Number(ENVS.freePlanId);

  const pid = String(projectId);
  const did = String(documentId);

  /**
   * The already-tagged refusal belongs to one project version. The dialog stays mounted when the
   * project or version changes, so the state carries its identity: it applies only while that
   * identity is current, and a late answer for an earlier identity is dropped.
   */
  const scriptIdentity = `${pid}:${did}`;
  const scriptIdentityRef = useRef(scriptIdentity);
  scriptIdentityRef.current = scriptIdentity;
  const [scriptTagged, setScriptTagged] = useState<ScriptTaggedState | null>(null);
  const currentScriptTagged = scriptTagged?.identity === scriptIdentity ? scriptTagged : null;
  /** The backend refused script breakdown on this version as already tagged. */
  const scriptKnownTagged = currentScriptTagged?.knownTagged ?? false;
  const taggedNotice = currentScriptTagged?.notice ?? null;

  const updateScriptTagged = useCallback(
    (identity: string, patch: Partial<Omit<ScriptTaggedState, "identity">>) => {
      if (identity !== scriptIdentityRef.current) return;
      setScriptTagged((previous) => ({
        ...(previous?.identity === identity
          ? previous
          : { identity, knownTagged: false, notice: null }),
        ...patch,
      }));
    },
    []
  );

  useEffect(() => {
    setScriptTagged(null);
  }, [open, scriptIdentity]);

  const taggedNoticeId = useId();
  const scriptRunButtonRef = useRef<HTMLButtonElement>(null);
  const characterRunButtonRef = useRef<HTMLButtonElement>(null);
  const coverageRunButtonRef = useRef<HTMLButtonElement>(null);
  const scheduleRunButtonRef = useRef<HTMLButtonElement>(null);
  const budgetRunButtonRef = useRef<HTMLButtonElement>(null);
  const advancedSummaryRef = useRef<HTMLElement>(null);

  // Opened for one pipeline: move focus to its Run button once the dialog
  // has placed its own initial focus. Nothing runs until the user clicks.
  useEffect(() => {
    if (!open || !preselectedPipeline) return;
    const runButtonRefs: Record<RunAiHubPipeline, RefObject<HTMLButtonElement>> = {
      script_breakdown: scriptRunButtonRef,
      character_breakdown: characterRunButtonRef,
      coverage: coverageRunButtonRef,
      schedule: scheduleRunButtonRef,
      budget: budgetRunButtonRef,
    };
    const frame = requestAnimationFrame(() => {
      const button = runButtonRefs[preselectedPipeline].current;
      button?.scrollIntoView?.({ block: "nearest" });
      if (button && !button.disabled) button.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, preselectedPipeline]);

  /** Closing the message removes the focused button, so focus goes back to script breakdown's Run. */
  const keepScriptTags = () => {
    updateScriptTagged(scriptIdentity, { notice: null });
    const runButton = scriptRunButtonRef.current;
    if (runButton && !runButton.disabled) runButton.focus();
    else advancedSummaryRef.current?.focus();
  };

  const { trackEvent } = useAnalytics();
  const hubOpenRef = useRef(false);

  useEffect(() => {
    if (open && !hubOpenRef.current) {
      trackEvent(ANALYTICS_EVENTS.SCRIPT_AI_HUB_OPENED, { project_id: pid, document_id: did });
    }
    if (!open && hubOpenRef.current) {
      trackEvent(ANALYTICS_EVENTS.SCRIPT_AI_HUB_CLOSED, { project_id: pid, document_id: did });
    }
    hubOpenRef.current = open;
  }, [did, open, pid, trackEvent]);

  useEffect(() => {
    if (open) {
      setScriptPipelinesAccordion("script-pipelines");
    }
  }, [open]);

  const trackRunRequested = useCallback(
    (pipeline_key: ScriptAiHubPipelineKey) => {
      trackEvent(ANALYTICS_EVENTS.SCRIPT_AI_HUB_RUN_REQUESTED, {
        project_id: pid,
        document_id: did,
        pipeline_key,
      });
    },
    [did, pid, trackEvent]
  );

  const trackRunFailed = useCallback(
    (pipeline_key: ScriptAiHubPipelineKey, error: unknown) => {
      trackEvent(ANALYTICS_EVENTS.SCRIPT_AI_HUB_RUN_FAILED, {
        project_id: pid,
        document_id: did,
        pipeline_key,
        error_code: scriptAiHubMutationErrorCode(error),
      });
    },
    [did, pid, trackEvent]
  );

  const { embeddedProjectLicense } = useProjectAuth(pid);
  const isUnlicensedProject = embeddedProjectLicense.isLicensed === false;
  const createProjectGuard = useCreateProjectPermissionGuard(project ?? undefined);

  const hubPipelines = useMemo(() => resolveRunAiHubPipelineVisibility(project ?? null), [project]);
  const canRunScriptAi = effectiveHasScriptBreakdownForRun({
    projectHasScriptBreakdown: hubPipelines.hasScriptBreakdown,
    versionPayload: versionSettingsPayload,
    scriptHasMinimalRunContent,
  });
  const canRunCharacterAi = effectiveHasCharacterBreakdownForRun({
    projectHasCharacterBreakdown: hubPipelines.hasCharacterBreakdown,
    versionPayload: versionSettingsPayload,
  });
  const canRunCoverageAi = effectiveHasCoverageForRun({
    projectHasCoverage: hubPipelines.hasCoverage,
    versionPayload: versionSettingsPayload,
  });

  const scriptAvail = useMemo(
    () => getAiCreditsActionRerunAvailability(project, KEY_SCRIPT),
    [project]
  );
  const characterAvail = useMemo(
    () => getAiCreditsActionRerunAvailability(project, KEY_CHARACTER),
    [project]
  );
  const coverageAvail = useMemo(
    () => getAiCreditsActionRerunAvailability(project, KEY_COVERAGE),
    [project]
  );
  const scheduleAvail = useMemo(
    () => getAiCreditsActionRerunAvailability(project, KEY_SCHEDULE),
    [project]
  );
  const budgetAvail = useMemo(
    () => getAiCreditsActionRerunAvailability(project, KEY_BUDGET),
    [project]
  );

  const hasCharacter = hubPipelines.hasCharacterBreakdown;
  const hasCoverage = hubPipelines.hasCoverage;
  const hasScript = hubPipelines.hasScriptBreakdown;
  const hasSchedule = hubPipelines.hasSchedule;
  const hasBudget = hubPipelines.hasBudget;

  const scriptPipelinesSubtitle = useMemo(() => {
    if (hasSchedule && hasBudget) {
      return "Script, character & coverage — schedule and budget run separately below.";
    }
    if (hasSchedule) {
      return "Script, character & coverage — schedule runs separately below.";
    }
    if (hasBudget) {
      return "Script, character & coverage — budget runs separately below.";
    }
    return "Script, character & coverage — expand to choose";
  }, [hasBudget, hasSchedule]);

  const tagsReport = useTagDefinitionsReport(pid, did, open && (hasSchedule || hasBudget));
  const hasAppliedTags = useMemo(
    () => tagDefinitionsReportHasAppliedUsage(tagsReport.data),
    [tagsReport.data]
  );

  const stripboardApi = useStripboardApi(false, open && hasSchedule);
  const budgetApi = useBudgetApi(open && hasBudget);

  const { sheetData, budgetDetail } = useMemo(() => {
    const bd = budgetApi.budgetData;
    if (!bd) {
      return { sheetData: [] as BudgetCategory[], budgetDetail: undefined };
    }
    const budget_details: Omit<BudgetI, "categories"> = {
      estimated_shoot_days: bd.estimated_shoot_days,
      assumptions: bd.assumptions,
      tax_incentives: bd.tax_incentives,
      contingency_percentage: bd.contingency_percentage,
      contingency_actual: bd.contingency_actual,
      contingency_locked: bd.contingency_locked,
      created_at: bd.created_at,
      budget_id: bd.budget_id,
      document_id: bd.document_id,
      bond_fee_percentage: bd.bond_fee_percentage,
      updated_at: bd.updated_at,
      currency: bd.currency,
      document: bd.document,
    };
    return { sheetData: bd.categories, budgetDetail: budget_details };
  }, [budgetApi.budgetData]);

  const mapStatuses = () =>
    project?.featureStatuses?.map((s) => ({
      feature_status_id: s.featureStatusId,
      project_id: String(project?.projectId ?? projectId ?? ""),
      feature: s.feature,
      status: s.status,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

  const scriptStatus = getProjectFeatureStatus(mapStatuses(), "script_breakdown");
  const characterStatus = getProjectFeatureStatus(mapStatuses(), "character_breakdown");
  const coverageStatus = getProjectFeatureStatus(mapStatuses(), "coverage");
  const scheduleStatus = getProjectFeatureStatus(
    mapProjectFeatureStatusesToFeatureStatus(project?.featureStatuses, project?.projectId ?? pid),
    "schedule"
  );
  const budgetStatus = getProjectFeatureStatus(
    mapProjectFeatureStatusesToFeatureStatus(project?.featureStatuses, project?.projectId ?? pid),
    "budget"
  );

  const isScriptAiRunning = isAiFeatureStatusRunning(scriptStatus?.status);
  const isCharacterAiRunning = isAiFeatureStatusRunning(characterStatus?.status);
  const isCoverageAiRunning = isAiFeatureStatusRunning(coverageStatus?.status);
  const isScheduleAiRunning = isAiFeatureStatusRunning(scheduleStatus?.status);
  const isBudgetAiRunning = isAiFeatureStatusRunning(budgetStatus?.status);
  const isTrialProject = Boolean(project?.isTrial);

  const scriptBreakdownCompleteForScheduleOrBudget =
    isScriptBreakdownCompleteForScheduleOrBudget(project);
  const characterBreakdownCompleteForScheduleOrBudget =
    isCharacterBreakdownCompleteForScheduleOrBudget(project);
  const scheduleBudgetPrerequisitesMet = areScheduleBudgetRunPrerequisitesMet(project);

  const scheduleBudgetTagsBlock =
    (hasSchedule || hasBudget) &&
    scheduleBudgetPrerequisitesMet &&
    (tagsReport.isLoading || tagsReport.isFetching || (tagsReport.isSuccess && !hasAppliedTags));

  const scheduleBudgetRunTooltip = useMemo(() => {
    if (!scriptBreakdownCompleteForScheduleOrBudget) {
      return SCHEDULE_BUDGET_PRIOR_SCRIPT_TOOLTIP;
    }
    if (!characterBreakdownCompleteForScheduleOrBudget) {
      return SCHEDULE_BUDGET_PRIOR_CHARACTER_TOOLTIP;
    }
    if (!(hasSchedule || hasBudget)) {
      return undefined;
    }
    if (tagsReport.isLoading || tagsReport.isFetching) {
      return SCHEDULE_BUDGET_TAGS_CHECKING_TOOLTIP;
    }
    if (tagsReport.isSuccess && !hasAppliedTags) {
      return SCHEDULE_BUDGET_REQUIRE_TAGS_TOOLTIP;
    }
    return undefined;
  }, [
    characterBreakdownCompleteForScheduleOrBudget,
    hasAppliedTags,
    hasBudget,
    hasSchedule,
    scriptBreakdownCompleteForScheduleOrBudget,
    tagsReport.isFetching,
    tagsReport.isLoading,
    tagsReport.isSuccess,
  ]);

  const stripboardRowCount = stripboardApi.stripboardData?.length ?? 0;

  const mergeBody = forceOverwriteAdvanced
    ? { merge_policy: AiRunMergePolicy.FORCE_OVERWRITE }
    : {};
  const mergeBodyForModals = forceOverwriteAdvanced
    ? { merge_policy: AiRunMergePolicy.FORCE_OVERWRITE }
    : undefined;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const persistDraftOrAbort = useCallback(async (): Promise<boolean> => {
    if (!persistDraftBeforeRun) return true;
    const ok = await persistDraftBeforeRun();
    if (!ok) {
      handleToast("Save your draft before running AI.", statusEnum.ERROR);
    }
    return ok;
  }, [persistDraftBeforeRun]);

  const startScriptBreakdown = (body: { merge_policy?: AiRunMergePolicy }) => {
    if (!canRunScriptAi || scriptAvail.exhausted) return;
    trackRunRequested(KEY_SCRIPT);
    const identity = scriptIdentity;
    updateScriptTagged(identity, { notice: null });
    void (async () => {
      try {
        if (!(await persistDraftOrAbort())) return;
        await runs.runScriptBreakdown.mutateAsync(body);
        handleToast("Script breakdown AI run started.", statusEnum.SUCCESS);
      } catch (e) {
        trackRunFailed(KEY_SCRIPT, e);
        if (isBreakdownAlreadyTaggedError(e)) {
          updateScriptTagged(identity, { knownTagged: true, notice: "single_run" });
        }
      }
    })();
  };

  const onRunScript = () => startScriptBreakdown(mergeBody);

  const onOverwriteScriptTags = () =>
    startScriptBreakdown({ merge_policy: AiRunMergePolicy.FORCE_OVERWRITE });

  const onRunCharacter = () => {
    if (!canRunCharacterAi || characterAvail.exhausted) return;
    trackRunRequested(KEY_CHARACTER);
    void (async () => {
      try {
        if (!(await persistDraftOrAbort())) return;
        await runs.runCharacterBreakdown.mutateAsync(mergeBody);
        handleToast("Character breakdown AI run started.", statusEnum.SUCCESS);
      } catch (e) {
        trackRunFailed(KEY_CHARACTER, e);
      }
    })();
  };

  const onRunCoverage = () => {
    if (!canRunCoverageAi || coverageAvail.exhausted) return;
    trackRunRequested(KEY_COVERAGE);
    void (async () => {
      try {
        if (!(await persistDraftOrAbort())) return;
        await runs.runCoverage.mutateAsync(mergeBody);
        handleToast("Coverage AI run started.", statusEnum.SUCCESS);
      } catch (e) {
        trackRunFailed(KEY_COVERAGE, e);
      }
    })();
  };

  const runAllOrder = useMemo(() => {
    const order: Array<"script_breakdown" | "character_breakdown" | "coverage"> = [];
    if (hasScript && canRunScriptAi) order.push("script_breakdown");
    if (hasCharacter && canRunCharacterAi) order.push("character_breakdown");
    if (hasCoverage && canRunCoverageAi) order.push("coverage");
    return order;
  }, [canRunCharacterAi, canRunCoverageAi, canRunScriptAi, hasCharacter, hasCoverage, hasScript]);

  /**
   * What Run batch starts and charges for. Script breakdown is left out once the backend has
   * refused it as already tagged, unless force overwrite is selected.
   */
  const batchOrder = useMemo(
    () =>
      runAllOrder.filter(
        (step) => step !== "script_breakdown" || !scriptKnownTagged || forceOverwriteAdvanced
      ),
    [forceOverwriteAdvanced, runAllOrder, scriptKnownTagged]
  );
  const batchSkipsTaggedScript = batchOrder.length < runAllOrder.length;

  const scriptPipelinesBatchAriaLabel = useMemo(() => {
    const labels: string[] = [];
    for (const step of batchOrder) {
      if (step === "script_breakdown") {
        labels.push("script breakdown");
      } else if (step === "character_breakdown") {
        labels.push("character breakdown");
      } else {
        labels.push("coverage");
      }
    }
    return labels.length > 0 ? `Run batch: ${labels.join(", ")}` : "Run batch";
  }, [batchOrder]);

  const remaining = project?.aiCredits?.remaining ?? 0;
  const subscriptionAiUnlimited = isRunAiCreditsUnlimited(project?.aiCredits);

  const totalRunAllCost = useMemo(() => {
    let sum = 0;
    for (const step of batchOrder) {
      if (step === "script_breakdown") sum += scriptAvail.cost;
      if (step === "character_breakdown") sum += characterAvail.cost;
      if (step === "coverage") sum += coverageAvail.cost;
    }
    return sum;
  }, [batchOrder, scriptAvail.cost, characterAvail.cost, coverageAvail.cost]);

  const canRunAll =
    runAllOrder.length > 1 &&
    batchOrder.length > 0 &&
    canAffordScriptPipelineBatch(remaining, totalRunAllCost, subscriptionAiUnlimited) &&
    batchOrder.every((step) => {
      if (step === "script_breakdown") return canRunScriptAi;
      if (step === "character_breakdown") return canRunCharacterAi;
      return canRunCoverageAi;
    });

  const scriptPipelinesBusy =
    runs.isAnyPending || isScriptAiRunning || isCharacterAiRunning || isCoverageAiRunning;

  const onRunAll = () => {
    if (scriptPipelinesBusy) {
      handleToast(
        "Wait for the current AI run to finish before starting a batch.",
        statusEnum.ERROR
      );
      return;
    }
    if (!canRunAll) {
      handleToast("Not enough AI credits for this batch.", statusEnum.ERROR);
      return;
    }
    const identity = scriptIdentity;
    void (async () => {
      setRunAllStep("starting");
      updateScriptTagged(identity, { notice: null });
      let skippedTaggedScript = batchSkipsTaggedScript;
      try {
        if (!(await persistDraftOrAbort())) return;
        trackEvent(ANALYTICS_EVENTS.SCRIPT_AI_HUB_RUN_ALL_STARTED, {
          project_id: pid,
          document_id: did,
          step_count: batchOrder.length,
        });
        for (const step of batchOrder) {
          setRunAllStep(step);
          if (step === "script_breakdown") {
            trackRunRequested(KEY_SCRIPT);
            try {
              await runs.runScriptBreakdown.mutateAsync(mergeBody);
            } catch (e) {
              // Refused at no charge: keep the existing tags and start the other pipelines.
              if (!isBreakdownAlreadyTaggedError(e)) throw e;
              trackRunFailed(KEY_SCRIPT, e);
              skippedTaggedScript = true;
              updateScriptTagged(identity, { knownTagged: true });
            }
          } else if (step === "character_breakdown") {
            trackRunRequested(KEY_CHARACTER);
            await runs.runCharacterBreakdown.mutateAsync(mergeBody);
          } else {
            trackRunRequested(KEY_COVERAGE);
            await runs.runCoverage.mutateAsync(mergeBody);
          }
        }
        if (skippedTaggedScript) updateScriptTagged(identity, { notice: "batch" });
        handleToast(
          skippedTaggedScript
            ? "Other script pipelines started. Script breakdown kept its existing tags."
            : "All script pipelines started.",
          statusEnum.SUCCESS
        );
      } catch (e) {
        trackRunFailed("run_all", e);
        // Script breakdown still did not run, but the other pipelines may not have started.
        if (skippedTaggedScript) updateScriptTagged(identity, { notice: "single_run" });
        handleToast("A pipeline failed to start.", statusEnum.ERROR);
      } finally {
        setRunAllStep(null);
      }
    })();
  };

  const staleness = project?.aiCredits?.staleness;
  const isStale = Boolean(
    staleness &&
    (staleness.script_breakdown_stale ||
      staleness.character_breakdown_stale ||
      staleness.coverage_stale ||
      staleness.schedule_stale ||
      staleness.budget_stale)
  );

  const handleUnlicensedScheduleBlocked = useCallback(() => {
    setScheduleModalOpen(false);
    setLicensePurchaseModalOpen(true);
  }, []);

  const formatMeta = (cost: number, isRunning: boolean, suffix?: string) => {
    if (isRunning) return "Running…";
    const parts = [`${cost} ${cost === 1 ? "credit" : "credits"}`];
    if (suffix) parts.push(suffix);
    return parts.join(" · ");
  };

  const scheduleButtonDisabled =
    isScheduleAiRunning ||
    isTrialProject ||
    isUnlicensedProject ||
    stripboardApi.isStripboardLoading ||
    scheduleAvail.exhausted ||
    !scheduleBudgetPrerequisitesMet ||
    scheduleBudgetTagsBlock;

  const budgetButtonDisabled =
    isBudgetAiRunning ||
    isTrialProject ||
    isUnlicensedProject ||
    (!isOneTimePurchase && isFreePlan) ||
    budgetApi.isBudgetLoading ||
    !budgetDetail ||
    budgetAvail.exhausted ||
    !scheduleBudgetPrerequisitesMet ||
    scheduleBudgetTagsBlock;

  const scheduleRowTooltip =
    scheduleButtonDisabled && scheduleBudgetRunTooltip
      ? scheduleBudgetRunTooltip
      : scheduleButtonDisabled && scheduleAvail.exhausted
        ? SCRIPT_AI_HUB_CREDITS_EXHAUSTED_TOOLTIP
        : undefined;

  const budgetRowTooltip =
    budgetButtonDisabled && scheduleBudgetRunTooltip
      ? scheduleBudgetRunTooltip
      : budgetButtonDisabled && budgetAvail.exhausted
        ? SCRIPT_AI_HUB_CREDITS_EXHAUSTED_TOOLTIP
        : undefined;

  const scriptPlayDisabled =
    !canRunScriptAi || scriptAvail.exhausted || isScriptAiRunning || runs.isAnyPending;

  const characterPlayDisabled =
    !canRunCharacterAi || characterAvail.exhausted || isCharacterAiRunning || runs.isAnyPending;

  const coveragePlayDisabled =
    !canRunCoverageAi || coverageAvail.exhausted || isCoverageAiRunning || runs.isAnyPending;

  const runAllFooterDisabled = runAllOrder.length > 1 && (scriptPipelinesBusy || !canRunAll);

  const runBatchTooltip = getRunBatchTooltip({
    disabled: runAllFooterDisabled,
    remaining,
    totalCost: totalRunAllCost,
    scriptPipelinesBusy,
    canRunAll,
    unlimited: subscriptionAiUnlimited,
  });

  const scriptRowTooltip = getScriptPipelineRowTooltip({
    disabled: scriptPlayDisabled,
    exhausted: scriptAvail.exhausted,
    isRerunning: isScriptAiRunning,
    isAnyPending: runs.isAnyPending,
    versionBlocked: !canRunScriptAi,
  });

  const characterRowTooltip = getScriptPipelineRowTooltip({
    disabled: characterPlayDisabled,
    exhausted: characterAvail.exhausted,
    isRerunning: isCharacterAiRunning,
    isAnyPending: runs.isAnyPending,
    versionBlocked: !canRunCharacterAi,
    versionBlockedMessage: SCRIPT_AI_HUB_VERSION_CHARACTER_OFF_TOOLTIP,
  });

  const coverageRowTooltip = getScriptPipelineRowTooltip({
    disabled: coveragePlayDisabled,
    exhausted: coverageAvail.exhausted,
    isRerunning: isCoverageAiRunning,
    isAnyPending: runs.isAnyPending,
    versionBlocked: !canRunCoverageAi,
    versionBlockedMessage: SCRIPT_AI_HUB_VERSION_COVERAGE_OFF_TOOLTIP,
  });

  const scriptPipelineRows = (
    <>
      {hasScript ? (
        <FeatureRow
          icon={<FileText className="h-3.5 w-3.5" />}
          label="Script breakdown"
          meta={formatMeta(
            scriptAvail.cost,
            isScriptAiRunning,
            !canRunScriptAi ? "off for version" : undefined
          )}
          isRunning={isScriptAiRunning}
          disabled={scriptPlayDisabled}
          onRun={onRunScript}
          runButtonRef={scriptRunButtonRef}
          preselected={preselectedPipeline === "script_breakdown"}
          tooltip={scriptRowTooltip}
          featureInfo={FEATURE_INFO_SCRIPT_BREAKDOWN}
          rowClassName="py-4"
        />
      ) : null}

      {hasCharacter ? (
        <FeatureRow
          icon={<Users className="h-3.5 w-3.5" />}
          label="Character breakdown"
          meta={formatMeta(
            characterAvail.cost,
            isCharacterAiRunning,
            !canRunCharacterAi ? "off for version" : undefined
          )}
          isRunning={isCharacterAiRunning}
          disabled={characterPlayDisabled}
          onRun={onRunCharacter}
          runButtonRef={characterRunButtonRef}
          preselected={preselectedPipeline === "character_breakdown"}
          tooltip={characterRowTooltip}
          featureInfo={FEATURE_INFO_CHARACTER_BREAKDOWN}
          rowClassName="py-4"
        />
      ) : null}

      {hasCoverage ? (
        <FeatureRow
          icon={<Layers className="h-3.5 w-3.5" />}
          label="Coverage"
          meta={formatMeta(
            coverageAvail.cost,
            isCoverageAiRunning,
            !canRunCoverageAi ? "off for version" : undefined
          )}
          isRunning={isCoverageAiRunning}
          disabled={coveragePlayDisabled}
          onRun={onRunCoverage}
          runButtonRef={coverageRunButtonRef}
          preselected={preselectedPipeline === "coverage"}
          tooltip={coverageRowTooltip}
          featureInfo={FEATURE_INFO_COVERAGE}
          rowClassName="py-4"
        />
      ) : null}
    </>
  );

  return (
    <>
      <Modal isOpen={open} onClose={() => onOpenChange(false)}>
        <ModalHeader
          title={
            <>
              <Zap className={cn("h-5 w-5 shrink-0", HUB_ZAP_CLASS)} aria-hidden />
              Run AI
            </>
          }
          onClose={close}
        />
        <ModalBody showBorders={false}>
          <div className="flex flex-col">
            {project?.aiCredits ? (
              <div className="mb-4 flex items-end justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold leading-none tabular-nums text-foreground">
                    {subscriptionAiUnlimited
                      ? "Unlimited"
                      : Math.max(0, Math.floor(project.aiCredits.remaining ?? 0))}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {subscriptionAiUnlimited ? "on plan" : "credits remaining"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {typeof project.aiCredits.usedTotal === "number" ? (
                    <span className="tabular-nums">
                      {Math.max(0, Math.floor(project.aiCredits.usedTotal))} used
                    </span>
                  ) : null}
                  <span className="inline-flex items-center rounded-full bg-muted/50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {getAiCreditsWalletBadge(project.aiCredits.walletKind)}
                  </span>
                </div>
              </div>
            ) : null}

            <p className="mb-4 text-xs text-muted-foreground">
              AI drafts editable first passes. It never rewrites your script or trains on it.
            </p>

            {isStale && staleness ? (
              <p className="pb-3 text-xs text-primary">
                Outputs may be stale (rev {staleness.working_revision}).
              </p>
            ) : null}
            {runAllStep ? (
              <p className="pb-3 text-xs text-muted-foreground">
                Running <span className="text-foreground">{runAllStep}</span>…
              </p>
            ) : null}

            <details className="pb-3">
              <summary
                ref={advancedSummaryRef}
                className="cursor-pointer text-xs text-muted-foreground"
              >
                Advanced
              </summary>
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-foreground/80">
                <input
                  type="checkbox"
                  checked={forceOverwriteAdvanced}
                  onChange={(e) => setForceOverwriteAdvanced(e.target.checked)}
                />
                Force overwrite my edits (full replace)
              </label>
            </details>

            {taggedNotice ? (
              <div
                role="group"
                aria-labelledby={taggedNoticeId}
                className="mb-3 flex flex-col gap-2 rounded-md border border-border bg-muted/50 px-3 py-2.5"
              >
                <p id={taggedNoticeId} role="status" className="text-xs text-foreground/80">
                  {SCRIPT_ALREADY_TAGGED_MESSAGE[taggedNotice]}
                </p>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="badge"
                    color="gray"
                    size="sm"
                    onClick={keepScriptTags}
                  >
                    Keep my tags
                  </Button>
                  <Button
                    type="button"
                    variant="badge"
                    color="brand"
                    size="sm"
                    disabled={scriptPlayDisabled}
                    onClick={onOverwriteScriptTags}
                    className="text-primary hover:text-primary"
                  >
                    Force overwrite my edits <span aria-hidden>·</span>{" "}
                    {formatMeta(scriptAvail.cost, false)}
                  </Button>
                </div>
              </div>
            ) : null}

            <div className={SCRIPT_AI_HUB_CARD_SURFACE}>
              <div className="flex flex-col">
                {runAllOrder.length > 1 ? (
                  <Accordion
                    type="single"
                    collapsible
                    value={scriptPipelinesAccordion}
                    onValueChange={setScriptPipelinesAccordion}
                    className="flex w-full flex-col gap-0"
                  >
                    <AccordionItem
                      value="script-pipelines"
                      className={SCRIPT_PIPELINES_ACCORDION_ITEM_CLASS}
                    >
                      <AccordionPrimitive.Header
                        className={cn(
                          "flex w-full items-center gap-2 px-3 py-3 transition-colors sm:gap-3",
                          "hover:bg-muted"
                        )}
                      >
                        <AccordionPrimitive.Trigger
                          className={cn(
                            "group flex min-w-0 flex-1 items-center gap-2 rounded-md text-left text-foreground outline-none transition-colors sm:gap-3",
                            "focus-visible:outline-none focus-visible:ring-0"
                          )}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-3">
                            <span
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/50 text-primary"
                              aria-hidden
                            >
                              <Zap className={cn("h-3.5 w-3.5", HUB_ZAP_CLASS)} />
                            </span>
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="truncate text-sm font-medium text-foreground">
                                Script pipelines
                              </span>
                              <span className="truncate text-xs text-muted-foreground">
                                {scriptPipelinesSubtitle}
                              </span>
                            </div>
                          </div>
                          <ChevronDown
                            aria-hidden
                            className={cn(
                              "chevron-icon h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                              "group-data-[state=open]:rotate-180"
                            )}
                          />
                        </AccordionPrimitive.Trigger>
                        {runAllFooterDisabled ? (
                          runBatchTooltip ? (
                            <Tooltip placement="top">
                              <TooltipTrigger asChild>
                                <span className="inline-flex shrink-0">
                                  <Button
                                    type="button"
                                    variant="badge"
                                    color="brand"
                                    size="sm"
                                    disabled
                                    className="gap-2 text-primary hover:text-primary"
                                    aria-label={scriptPipelinesBatchAriaLabel}
                                  >
                                    <Zap className={cn("h-3.5 w-3.5", HUB_ZAP_CLASS)} aria-hidden />
                                    Run batch
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{runBatchTooltip}</TooltipContent>
                            </Tooltip>
                          ) : (
                            <Button
                              type="button"
                              variant="badge"
                              color="brand"
                              size="sm"
                              disabled
                              className="shrink-0 gap-2 text-primary hover:text-primary"
                              aria-label={scriptPipelinesBatchAriaLabel}
                            >
                              <Zap className={cn("h-3.5 w-3.5", HUB_ZAP_CLASS)} aria-hidden />
                              Run batch
                            </Button>
                          )
                        ) : (
                          <Button
                            type="button"
                            variant="badge"
                            color="brand"
                            size="sm"
                            onClick={onRunAll}
                            className="shrink-0 gap-2 text-primary hover:text-primary"
                            aria-label={scriptPipelinesBatchAriaLabel}
                          >
                            <Zap className={cn("h-3.5 w-3.5", HUB_ZAP_CLASS)} aria-hidden />
                            Run batch
                          </Button>
                        )}
                      </AccordionPrimitive.Header>
                      <AccordionContent className="p-0">
                        <div className="divide-y divide-border border-t border-border px-3">
                          {scriptPipelineRows}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <div className="divide-y divide-border px-3">{scriptPipelineRows}</div>
                )}

                {hasSchedule ? (
                  <PermissionGuard permission={Permissions.PROJECT_UPDATE} mode="tooltip">
                    <div className="border-t border-border px-3">
                      <FeatureRow
                        icon={<Calendar className="h-3.5 w-3.5 fill-none" aria-hidden />}
                        label="Schedule"
                        meta={formatMeta(
                          scheduleAvail.cost,
                          isScheduleAiRunning,
                          SCHEDULE_BUDGET_META_HELPER
                        )}
                        isRunning={isScheduleAiRunning}
                        disabled={scheduleButtonDisabled}
                        onRun={() => {
                          if (isUnlicensedProject) {
                            setLicensePurchaseModalOpen(true);
                            return;
                          }
                          if (!scheduleButtonDisabled) setScheduleModalOpen(true);
                        }}
                        runButtonRef={scheduleRunButtonRef}
                        preselected={preselectedPipeline === "schedule"}
                        tooltip={scheduleRowTooltip}
                        featureInfo={FEATURE_INFO_SCHEDULE}
                        iconPlacement="inline-with-title"
                      />
                    </div>
                  </PermissionGuard>
                ) : null}

                {hasBudget ? (
                  <PermissionGuard permission={Permissions.PROJECT_UPDATE} mode="tooltip">
                    <div className="border-t border-border px-3">
                      <FeatureRow
                        icon={<DollarSign className="h-3.5 w-3.5 fill-none" aria-hidden />}
                        label="Budget"
                        meta={formatMeta(
                          budgetAvail.cost,
                          isBudgetAiRunning,
                          SCHEDULE_BUDGET_META_HELPER
                        )}
                        isRunning={isBudgetAiRunning}
                        disabled={budgetButtonDisabled}
                        onRun={() => {
                          if (isUnlicensedProject) {
                            setLicensePurchaseModalOpen(true);
                            return;
                          }
                          if (!budgetButtonDisabled) setBudgetModalOpen(true);
                        }}
                        runButtonRef={budgetRunButtonRef}
                        preselected={preselectedPipeline === "budget"}
                        tooltip={budgetRowTooltip}
                        featureInfo={FEATURE_INFO_BUDGET}
                        iconPlacement="inline-with-title"
                      />
                    </div>
                  </PermissionGuard>
                ) : null}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full items-center justify-end gap-2">
            <Button type="button" variant="badge" color="gray" size="sm" onClick={close}>
              Close
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {scheduleModalOpen && hasSchedule ? (
        <ReRunDayBreaksModal
          open={scheduleModalOpen}
          setOpen={() => setScheduleModalOpen(false)}
          projectId={pid}
          documentId={did}
          reloadStripboard={stripboardApi.refetchStripboard}
          onUnlicensedScheduleBlocked={handleUnlicensedScheduleBlocked}
          daybreaksOverride={stripboardApi.daybreaks}
          stripboardRowCountOverride={stripboardRowCount}
          scriptHasMinimalRunContent={scriptHasMinimalRunContent}
          scheduleMergePayload={mergeBodyForModals}
          onPipelineRunRequested={() => {
            trackRunRequested(KEY_SCHEDULE);
          }}
        />
      ) : null}

      {budgetModalOpen && hasBudget && budgetDetail ? (
        <EditBudgetAssumptionsModal
          open={budgetModalOpen}
          setOpen={() => setBudgetModalOpen(false)}
          lockedCategories={[]}
          sheetData={sheetData}
          budgetDetail={budgetDetail}
          handleUpdateBudgetCurrency={(currency) => budgetApi.updateBudgetCurrency.mutate(currency)}
          scriptHasMinimalRunContent={scriptHasMinimalRunContent}
          budgetMergePayload={mergeBodyForModals}
          onPipelineRunRequested={() => {
            trackRunRequested(KEY_BUDGET);
          }}
        />
      ) : null}

      {project?.projectId ? (
        <PurchaseProjectModal
          open={licensePurchaseModalOpen}
          onClose={() => setLicensePurchaseModalOpen(false)}
          mode={createProjectGuard.permissions.purchaseModalMode}
          projectId={String(project.projectId)}
        />
      ) : null}
    </>
  );
};
